import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Tooltip,
  Badge,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Article as ArticleIcon,
  Park as ParkIcon,
  Store as StoreIcon,
  UploadFile as UploadFileIcon,
  Campaign as CampaignIcon,
  Visibility as VisibilityIcon,
  Category as CategoryIcon,
  Inbox as InboxIcon,
  Assignment as AssignmentIcon,
  Payments as PaymentsIcon,
  Report as ReportIcon,
  SmartToy as SmartToyIcon,
  Gavel as GavelIcon,
  SystemUpdate as SystemUpdateIcon,
  Logout as LogoutIcon,
  Pets as PetsIcon,
  ChevronRight as ChevronRightIcon,
  LocationCity as LocationCityIcon,
  Tune as TuneIcon,
  Percent as PercentIcon,
  AssignmentReturn as AssignmentReturnIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
} from '@mui/icons-material';
import logoImage from '../logo.png';

const DRAWER_WIDTH = 256;
const MINI_WIDTH = 68;

const S = {
  bg: '#18123a',
  text: 'rgba(255,255,255,0.62)',
  activeText: '#fff',
  activeBg: 'rgba(139,92,246,0.18)',
  activeBorder: '#8B5CF6',
  hover: 'rgba(255,255,255,0.05)',
  divider: 'rgba(255,255,255,0.08)',
  sectionLabel: 'rgba(255,255,255,0.28)',
};

const NAV_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { title: 'Dashboard', Icon: DashboardIcon, path: '/dashboard' },
    ],
  },
  {
    label: 'Content',
    items: [
      { title: 'Users',  Icon: PeopleIcon,  path: '/users' },
      { title: 'Dogs',   Icon: PetsIcon,    path: '/dogs' },
      { title: 'Dog Attributes', Icon: TuneIcon, path: '/dog-attributes' },
      { title: 'Posts',  Icon: ArticleIcon, path: '/posts' },
      { title: 'Dog Gardens', Icon: ParkIcon,    path: '/dog-gardens' },
    ],
  },
  {
    label: 'Business',
    items: [
      { title: 'Providers',          Icon: StoreIcon,      path: '/providers' },
      { title: 'Claims',             Icon: AssignmentIcon, path: '/provider-claims' },
      { title: 'Checkout Review',    Icon: AssignmentIcon, path: '/checkout-review' },
      { title: 'Provider Import',    Icon: UploadFileIcon, path: '/provider-import' },
      { title: 'Provider Landing',   Icon: CampaignIcon,   path: '/provider-landing-config' },
      { title: 'Visibility',         Icon: VisibilityIcon, path: '/provider-visibility' },
      { title: 'Categories',         Icon: CategoryIcon,   path: '/product-categories' },
      { title: 'Store Commission',   Icon: PercentIcon,    path: '/store-commission' },
      { title: 'Store Returns',      Icon: AssignmentReturnIcon, path: '/store-returns' },
      { title: 'Payouts',            Icon: AccountBalanceWalletIcon, path: '/store-payouts' },
    ],
  },
  {
    label: 'Moderation',
    items: [
      { title: 'Submissions', Icon: InboxIcon,  path: '/submissions', hasBadge: true },
      { title: 'Reports',     Icon: ReportIcon, path: '/reports' },
      { title: 'Payment Reviews', Icon: PaymentsIcon, path: '/payment-reviews' },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { title: 'AI Agent',          Icon: SmartToyIcon,     path: '/ai-agent-config' },
      { title: 'Legal Documents',   Icon: GavelIcon,        path: '/legal-documents' },
      { title: 'Cities Management', Icon: LocationCityIcon, path: '/cities-management' },
      { title: 'App Versions',      Icon: SystemUpdateIcon, path: '/app-version-config' },
    ],
  },
];

const PAGE_TITLES = {
  '/dashboard':              'Dashboard',
  '/users':                  'Users',
  '/dogs':                   'Dogs',
  // /dogs/:id handled by getPageTitle fallback
  '/posts':                  'Posts',
  '/dog-gardens':            'Dog Gardens',
  '/providers':              'Providers',
  '/provider-import':        'Provider Import',
  '/provider-claims':        'Provider Claims',
  '/checkout-review':        'Checkout Review',
  '/provider-landing-config':'Provider Landing Page',
  '/provider-visibility':    'Provider Visibility',
  '/product-categories':     'Product Categories',
  '/store-commission':       'Store Commission',
  '/store-returns':          'Store Returns & Late Shipments',
  '/store-payouts':          'Store Payouts',
  '/submissions':            'Submissions',
  '/reports':                'Reports',
  '/ai-agent-config':        'AI Agent Configuration',
  '/legal-documents':        'Legal Documents',
  '/cities-management':      'Cities Management',
  '/dog-attributes':         'Dog Attributes',
  '/app-version-config':     'App Version Compatibility',
};

function getPageTitle(pathname) {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.startsWith('/dogs/'))        return 'Dog Details';
  if (pathname.startsWith('/users/'))       return 'User Details';
  if (pathname.startsWith('/posts/'))       return 'Post Details';
  if (pathname.startsWith('/dog-gardens/')) return 'Garden Details';
  if (pathname.startsWith('/providers/'))  return 'Provider Details';
  if (pathname.startsWith('/orders/'))     return 'Order Details';
  return 'Anipaw Admin';
}

function NavItem({ item, isActive, mini, pendingCount, navigate }) {
  const { Icon } = item;
  const badgeCount = item.hasBadge ? pendingCount : 0;

  const button = (
    <ListItemButton
      onClick={() => navigate(item.path)}
      sx={{
        mx: mini ? 0.5 : 1,
        my: 0.15,
        px: mini ? 0 : 1.5,
        py: 0.85,
        borderRadius: 1.5,
        borderLeft: !mini && isActive ? `3px solid ${S.activeBorder}` : '3px solid transparent',
        bgcolor: isActive ? S.activeBg : 'transparent',
        color: isActive ? S.activeText : S.text,
        justifyContent: mini ? 'center' : 'flex-start',
        minHeight: 40,
        transition: 'background-color 0.15s, color 0.15s',
        '&:hover': {
          bgcolor: isActive ? S.activeBg : S.hover,
          color: S.activeText,
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: mini ? 'unset' : 34,
          color: 'inherit',
          justifyContent: 'center',
        }}
      >
        <Badge
          badgeContent={badgeCount > 0 ? badgeCount : undefined}
          color="error"
          max={99}
          sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16 } }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Badge>
      </ListItemIcon>
      {!mini && (
        <ListItemText
          primary={item.title}
          primaryTypographyProps={{
            fontSize: '0.85rem',
            fontWeight: isActive ? 600 : 400,
            letterSpacing: isActive ? 0 : '-0.01em',
          }}
        />
      )}
    </ListItemButton>
  );

  return (
    <ListItem disablePadding>
      {mini ? (
        <Tooltip title={item.title} placement="right" arrow>
          <Box sx={{ width: '100%' }}>{button}</Box>
        </Tooltip>
      ) : (
        button
      )}
    </ListItem>
  );
}

function SidebarContent({ mini, pendingCount, navigate, location, onClose, currentUser, onLogout }) {
  const initials = currentUser?.email?.charAt(0).toUpperCase() ?? 'A';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: S.bg, overflow: 'hidden' }}>
      {/* Logo */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 64,
          px: mini ? 0 : 2.5,
          justifyContent: mini ? 'center' : 'flex-start',
          gap: 1.5,
          flexShrink: 0,
          borderBottom: `1px solid ${S.divider}`,
        }}
      >
        <Avatar
          src={logoImage}
          alt="Anipaw"
          sx={{ width: 32, height: 32, flexShrink: 0 }}
          imgProps={{ draggable: false }}
        />
        {!mini && (
          <Box sx={{ overflow: 'hidden' }}>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.92rem', lineHeight: 1.25, letterSpacing: '-0.01em' }}>
              Anipaw
            </Typography>
            <Typography sx={{ color: S.sectionLabel, fontSize: '0.68rem', lineHeight: 1 }}>
              Admin Panel
            </Typography>
          </Box>
        )}
      </Box>

      {/* Nav */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1,
          '&::-webkit-scrollbar': { width: 3 },
          '&::-webkit-scrollbar-thumb': { bgcolor: S.divider, borderRadius: 4 },
        }}
      >
        {NAV_SECTIONS.map((section, idx) => {
          const isActive = (item) => {
            if (location.pathname === item.path) return true;
            if (item.path !== '/dashboard' && location.pathname.startsWith(item.path + '/')) return true;
            return false;
          };
          return (
            <Box key={section.label}>
              {idx > 0 && mini && (
                <Divider sx={{ borderColor: S.divider, mx: 1, my: 0.5 }} />
              )}
              {!mini && (
                <Typography
                  sx={{
                    px: 3,
                    pt: idx === 0 ? 0.5 : 1.5,
                    pb: 0.25,
                    display: 'block',
                    color: S.sectionLabel,
                    fontWeight: 700,
                    fontSize: '0.6rem',
                    letterSpacing: '0.09em',
                    textTransform: 'uppercase',
                  }}
                >
                  {section.label}
                </Typography>
              )}
              <List disablePadding dense>
                {section.items.map((item) => (
                  <NavItem
                    key={item.path}
                    item={item}
                    isActive={isActive(item)}
                    mini={mini}
                    pendingCount={pendingCount}
                    navigate={navigate}
                  />
                ))}
              </List>
            </Box>
          );
        })}
      </Box>

      {/* User footer */}
      <Box
        sx={{
          borderTop: `1px solid ${S.divider}`,
          px: mini ? 0.5 : 1.5,
          py: 1.25,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: mini ? 'center' : 'space-between',
          gap: 1,
        }}
      >
        {mini ? (
          <Tooltip title="Logout" placement="right" arrow>
            <IconButton
              size="small"
              onClick={onLogout}
              sx={{ color: S.text, '&:hover': { color: '#fff', bgcolor: S.hover } }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Avatar
                sx={{
                  width: 30, height: 30,
                  bgcolor: alpha('#8B5CF6', 0.5),
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  color: '#fff',
                  flexShrink: 0,
                }}
              >
                {initials}
              </Avatar>
              <Typography
                sx={{
                  color: S.text,
                  fontSize: '0.78rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {currentUser?.email ?? ''}
              </Typography>
            </Box>
            <Tooltip title="Logout">
              <IconButton
                size="small"
                onClick={onLogout}
                sx={{ color: S.text, flexShrink: 0, '&:hover': { color: '#fff', bgcolor: S.hover } }}
              >
                <LogoutIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
}

const Layout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mini, setMini] = useState(false);
  const { signOut, currentUser } = useAuth();
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.moderation.getCounts()
      .then((data) => { if (!cancelled) setPendingCount(data?.total ?? 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api]);

  const handleLogout = async () => {
    try { await signOut(); navigate('/login'); } catch (e) { console.error(e); }
  };

  const drawerWidth = mini ? MINI_WIDTH : DRAWER_WIDTH;
  const pageTitle = getPageTitle(location.pathname);

  const drawerContent = (
    <SidebarContent
      mini={isMobile ? false : mini}
      pendingCount={pendingCount}
      navigate={navigate}
      location={location}
      onClose={() => setMobileOpen(false)}
      currentUser={currentUser}
      onLogout={handleLogout}
    />
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Desktop permanent sidebar */}
      {!isMobile && (
        <Box
          component="nav"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: mini
                ? theme.transitions.duration.leavingScreen
                : theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <Drawer
            variant="permanent"
            sx={{
              width: drawerWidth,
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                border: 'none',
                overflowX: 'hidden',
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: mini
                    ? theme.transitions.duration.leavingScreen
                    : theme.transitions.duration.enteringScreen,
                }),
              },
            }}
          >
            {drawerContent}
          </Drawer>
        </Box>
      )}

      {/* Mobile temporary sidebar */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              border: 'none',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
            zIndex: (t) => t.zIndex.drawer - 1,
          }}
        >
          <Toolbar sx={{ minHeight: '56px !important', px: { xs: 2, sm: 3 } }}>
            <IconButton
              edge="start"
              onClick={() => isMobile ? setMobileOpen(true) : setMini(!mini)}
              sx={{ mr: 1.5, color: 'text.secondary', borderRadius: 1.5 }}
              size="small"
            >
              {!isMobile && !mini ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.95rem', letterSpacing: '-0.01em' }}
            >
              {pageTitle}
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Page content */}
        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, maxWidth: '100%', overflowX: 'hidden' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
